// =============================================================
// MapAnnotations.js — Shared GIS annotation engine
//
// Reusable across the per-property/farm parcel editor (ParcelsMapTab, editable)
// and the global map (MapView, read-only). Provides:
//   • ANNOTATION_CATEGORIES — drawable land-feature types (food plots, duck
//     holes, levees, routes, waypoints, labels) with default geometry + color.
//   • setupAnnotationLayers(map, fc) — idempotently add/update the annotation
//     source + fill / outline / line / point / label layers.
//   • <AnnotationPanel /> — the editing toolbar + list, reusing the host map's
//     existing MapboxDraw instance to draw / measure / edit / delete features,
//     persisted via /api/map-features.
//
// Geometry is plain GeoJSON. Circles/squares are stored as Polygons; their
// radius lives in style.radiusMeters (Phase 2 radial-drag mode).
// =============================================================
import { useState, useEffect, useRef, useCallback } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { Btn, useToast, Icon } from '../ui';
import { ensureTurf, kmzToGeoJSON } from './mapLibs';
import { fetchDirections } from './mapOverlays';

// ── Category catalog ─────────────────────────────────────────
// kind = natural geometry primitive; color = default fill/stroke.
export const ANNOTATION_CATEGORIES = [
  { key: 'food-plot', label: 'Food Plot', kind: 'polygon', color: '#4ade80', icon: '🌱' },
  { key: 'duck-hole', label: 'Duck Hole', kind: 'polygon', color: '#38bdf8', icon: '🦆' },
  { key: 'levee',     label: 'Levee',     kind: 'line',    color: '#b45309', icon: '〰️' },
  { key: 'route',     label: 'Route',     kind: 'line',    color: '#f59e0b', icon: '🛣️' },
  { key: 'waypoint',  label: 'Waypoint',  kind: 'point',   color: '#ef4444', icon: '📍' },
  { key: 'label',     label: 'Label',     kind: 'point',   color: '#7c3aed', icon: '🏷️' },
  { key: 'poi',       label: 'Point of Interest', kind: 'point', color: '#0ea5e9', icon: '⭐' },
  { key: 'other',     label: 'Area',      kind: 'polygon', color: '#22d3ee', icon: '⬡' },
];
// Categories that aren't drawn from the toolbar but still need display metadata
// (e.g. GPS tracks created by the tracking tool).
const EXTRA_CATEGORIES = [
  { key: 'track', label: 'GPS Track', kind: 'line', color: '#16a34a', icon: '●' },
];
export const CATEGORY_MAP = Object.fromEntries(
  [...ANNOTATION_CATEGORIES, ...EXTRA_CATEGORIES].map(c => [c.key, c]),
);

const catFor   = (key) => CATEGORY_MAP[key] || CATEGORY_MAP.other;
const modeFor  = (kind) => kind === 'polygon' ? 'draw_polygon' : kind === 'line' ? 'draw_line_string' : 'draw_point';

// ── Fill styles + color palette (Phase 2 styling) ───────────
export const FILL_STYLES = ['none', 'hatch', 'cross'];   // none = solid shading
export const COLOR_PALETTE = ['#4ade80', '#38bdf8', '#f59e0b', '#ef4444', '#7c3aed', '#0ea5e9', '#b45309', '#22d3ee', '#ffffff'];

// Stable id for a generated hatch pattern image.
const hatchId = (hatch, color) => `hatch-${hatch}-${String(color).replace('#', '')}`;

// Build a small repeating diagonal-hatch (or cross-hatch) tile in the given
// color, returned as ImageData for map.addImage. Lines wrap the tile edges so
// the pattern tiles seamlessly.
function makeHatchImageData(color, cross) {
  const S = 8;
  const canvas = document.createElement('canvas');
  canvas.width = S; canvas.height = S;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, S, S);
  ctx.strokeStyle = color; ctx.lineWidth = 1.4;
  const diag = (x0, y0, x1, y1) => { ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke(); };
  diag(-1, 1, 1, -1); diag(0, S, S, 0); diag(S - 1, S + 1, S + 1, S - 1);   // ╱
  if (cross) { diag(-1, S - 1, 1, S + 1); diag(0, 0, S, S); diag(S - 1, -1, S + 1, 1); } // ╲
  return ctx.getImageData(0, 0, S, S);
}

// Register any hatch pattern images referenced by the feature set that aren't
// already loaded on the map.
function ensureHatchPatterns(map, features) {
  for (const f of features) {
    const hatch = f.properties?._hatch;
    const color = f.properties?._fill;
    if (!hatch || hatch === 'none') continue;
    const id = hatchId(hatch, color);
    if (map.hasImage(id)) continue;
    try { map.addImage(id, makeHatchImageData(color, hatch === 'cross'), { pixelRatio: 1 }); } catch { /* ignore */ }
  }
}

// Flatten each feature's style into top-level properties Mapbox can read in
// data-driven paint expressions (Mapbox can't read nested objects).
export function decorateAnnotationFC(fc) {
  const features = (fc?.features || []).map(f => {
    const p     = f.properties || {};
    const cat   = catFor(p.category);
    const style = p.style || {};
    const fill  = style.fillColor || style.color || cat.color;
    const hatch = FILL_STYLES.includes(style.hatch) ? style.hatch : 'none';
    return {
      ...f,
      properties: {
        ...p,
        _color:       style.strokeColor || style.fillColor || style.color || cat.color,
        _fill:        fill,
        _fillOpacity: style.opacity != null ? style.opacity : 0.35,
        _strokeWidth: style.strokeWidth || 2.5,
        _hatch:       hatch,
        _pattern:     hatch === 'none' ? '' : hatchId(hatch, fill),
        _label:       p.name || '',
      },
    };
  });
  return { type: 'FeatureCollection', features };
}

const ANNOTATION_LAYER_IDS = [
  'annotations-fill', 'annotations-fill-hatch', 'annotations-outline',
  'annotations-line', 'annotations-points', 'annotations-labels',
];

// Idempotently (re)create the annotation source + layers. Safe to call after a
// style switch (re-adds source/layers when missing, else just setData).
export function setupAnnotationLayers(map, fc) {
  if (!map) return;
  const data = decorateAnnotationFC(fc);
  ensureHatchPatterns(map, data.features);

  const existing = map.getSource('annotations');
  if (existing) { existing.setData(data); return; }

  map.addSource('annotations', { type: 'geojson', data });

  const isPoly = ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false];
  const isLine = ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false];
  const isPoint = ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false];

  // Solid (shaded) polygons — no hatch.
  map.addLayer({
    id: 'annotations-fill', type: 'fill', source: 'annotations',
    filter: ['all', isPoly, ['==', ['get', '_pattern'], '']],
    paint: { 'fill-color': ['get', '_fill'], 'fill-opacity': ['get', '_fillOpacity'] },
  });
  // Hatched / cross-hatched polygons — fill-pattern overrides color.
  map.addLayer({
    id: 'annotations-fill-hatch', type: 'fill', source: 'annotations',
    filter: ['all', isPoly, ['!=', ['get', '_pattern'], '']],
    paint: { 'fill-pattern': ['get', '_pattern'] },
  });
  map.addLayer({
    id: 'annotations-outline', type: 'line', source: 'annotations',
    filter: isPoly,
    paint: { 'line-color': ['get', '_color'], 'line-width': ['get', '_strokeWidth'] },
  });
  map.addLayer({
    id: 'annotations-line', type: 'line', source: 'annotations',
    filter: isLine,
    paint: { 'line-color': ['get', '_color'], 'line-width': ['get', '_strokeWidth'] },
  });
  map.addLayer({
    id: 'annotations-points', type: 'circle', source: 'annotations',
    filter: isPoint,
    paint: {
      'circle-color': ['get', '_color'],
      'circle-radius': 6,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  });
  map.addLayer({
    id: 'annotations-labels', type: 'symbol', source: 'annotations',
    layout: {
      'text-field': ['get', '_label'],
      'text-size': 12,
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-anchor': 'top',
      'text-offset': [0, 0.8],
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#1a3844',
      'text-halo-width': 1.5,
    },
  });
}

export function removeAnnotationLayers(map) {
  if (!map) return;
  ANNOTATION_LAYER_IDS.forEach(id => { if (map.getLayer(id)) map.removeLayer(id); });
  if (map.getSource('annotations')) map.removeSource('annotations');
}

// ── Dashed preview overlay (circle/square drag, trim) ────────
function showPreview(map, geometry, color) {
  if (!map) return;
  const data = geometry
    ? { type: 'Feature', properties: {}, geometry }
    : { type: 'FeatureCollection', features: [] };
  const src = map.getSource('annotation-preview');
  if (src) { src.setData(data); return; }
  map.addSource('annotation-preview', { type: 'geojson', data });
  map.addLayer({ id: 'annotation-preview-fill', type: 'fill', source: 'annotation-preview',
    paint: { 'fill-color': color || '#f59e0b', 'fill-opacity': 0.2 } });
  map.addLayer({ id: 'annotation-preview-line', type: 'line', source: 'annotation-preview',
    paint: { 'line-color': color || '#f59e0b', 'line-width': 2, 'line-dasharray': [2, 1] } });
}
function clearPreview(map) {
  const src = map?.getSource('annotation-preview');
  if (src) src.setData({ type: 'FeatureCollection', features: [] });
}

// ── Snapping (turf nearest-point-on-line against loaded geometry) ──
// Build snap-target lines from owned parcels + existing annotations in view.
function getSnapTargets(map, turf) {
  const lines = [];
  ['parcels', 'annotations'].forEach(srcId => {
    if (!map.getSource(srcId)) return;
    let feats = [];
    try { feats = map.querySourceFeatures(srcId); } catch { feats = []; }
    for (const f of feats) {
      const g = f.geometry;
      if (!g) continue;
      if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
        try {
          const l = turf.polygonToLine(f);
          if (l.type === 'FeatureCollection') (l.features || []).forEach(x => lines.push(x));
          else lines.push(l);
        } catch { /* ignore */ }
      } else if (g.type === 'LineString') {
        lines.push(f);
      }
    }
  });
  return lines;
}

// Snap each vertex of a geometry to the nearest target line within tolerance.
function snapGeometry(turf, geometry, targetLines, toleranceMeters = 15) {
  if (!targetLines.length) return geometry;
  const snapPt = (lng, lat) => {
    const pt = turf.point([lng, lat]);
    let best = null, bestD = Infinity;
    for (const line of targetLines) {
      try {
        const np = turf.nearestPointOnLine(line, pt, { units: 'meters' });
        if (np.properties.dist < bestD) { bestD = np.properties.dist; best = np; }
      } catch { /* ignore */ }
    }
    return (best && bestD <= toleranceMeters) ? best.geometry.coordinates : [lng, lat];
  };
  const g = geometry;
  if (g.type === 'Point')      return { ...g, coordinates: snapPt(g.coordinates[0], g.coordinates[1]) };
  if (g.type === 'LineString') return { ...g, coordinates: g.coordinates.map(c => snapPt(c[0], c[1])) };
  if (g.type === 'Polygon')    return { ...g, coordinates: g.coordinates.map(ring => ring.map(c => snapPt(c[0], c[1]))) };
  return g;
}

// Format a turf length (miles) as feet or miles.
function fmtDistance(miles) {
  const feet = miles * 5280;
  return feet < 1000 ? `${feet.toFixed(0)} ft` : `${miles.toFixed(2)} mi`;
}
const sqMetersToAcres = (m2) => m2 * 0.000247105;

// Convert a parsed KMZ/KML FeatureCollection into map-feature payloads, keeping
// every shape (points, lines, polygons) — not just closed parcel boundaries.
// Multi-part geometries are split into single-part features so each renders and
// edits cleanly. Returns [{ featureKind, category, name, geometry }].
export function kmzFeaturesToPayloads(fc) {
  const out = [];
  const push = (featureKind, category, geometry, name) => out.push({ featureKind, category, geometry, name: name || null });
  const handle = (g, name) => {
    if (!g || !g.type) return;
    switch (g.type) {
      case 'Point':           push('point', 'waypoint', g, name); break;
      case 'MultiPoint':      (g.coordinates || []).forEach(c => push('point', 'waypoint', { type: 'Point', coordinates: c }, name)); break;
      case 'LineString':      push('line', 'other', g, name); break;
      case 'MultiLineString': (g.coordinates || []).forEach(c => push('line', 'other', { type: 'LineString', coordinates: c }, name)); break;
      case 'Polygon':         push('polygon', 'other', g, name); break;
      case 'MultiPolygon':    (g.coordinates || []).forEach(c => push('polygon', 'other', { type: 'Polygon', coordinates: c }, name)); break;
      case 'GeometryCollection': (g.geometries || []).forEach(gg => handle(gg, name)); break;
      default: break;
    }
  };
  for (const f of (fc?.features || [])) handle(f.geometry, f.properties?.name);
  return out;
}

// ---------------------------------------------------------------------------
// <AnnotationPanel /> — editable toolbar + feature list
//
// Reuses the host map's MapboxDraw instance (drawRef). Only one of parcel-draw
// (parent) or annotation-draw (here) is active at a time — pass parcelDrawActive
// so the tools disable while a parcel boundary is being drawn.
// ---------------------------------------------------------------------------
export const AnnotationPanel = ({ mapRef, drawRef, mapReady, ownerType, ownerId, parcelDrawActive, defaultCollapsed = false }) => {
  const { toast } = useToast();
  const [fc,        setFc]        = useState({ type: 'FeatureCollection', features: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [drawSpec,  setDrawSpec]  = useState(null);   // { category, kind } while drawing
  const [measure,   setMeasure]   = useState(null);   // { kind } while measuring
  const [measureResult, setMeasureResult] = useState(null);
  const [editingId, setEditingId] = useState(null);   // feature id being vertex-edited
  const [shapeMode, setShapeMode] = useState(null);   // 'circle' | 'square' while radial-dragging
  const [snap,      setSnap]      = useState(false);  // snap drawn vertices to nearby geometry
  const [trimPreview, setTrimPreview] = useState(null); // { id, geometry } awaiting confirm
  const [busy,      setBusy]      = useState(false);
  const [show,      setShow]      = useState(!defaultCollapsed);

  // Phase 3 — route builder + GPS tracking
  const [routeMode, setRouteMode] = useState(false);
  const [routePts,  setRoutePts]  = useState([]);       // clicked waypoints [lng,lat]
  const [routeGeom, setRouteGeom] = useState(null);     // computed route geometry
  const [routeProfile, setRouteProfile] = useState('driving');
  const [tracking,  setTracking]  = useState(false);
  const [trackLen,  setTrackLen]  = useState(0);
  const [kmzBusy,   setKmzBusy]   = useState(false);
  const kmzInputRef = useRef(null);
  const watchIdRef  = useRef(null);
  const trackRef    = useRef([]);
  const routeModeRef = useRef(false);
  useEffect(() => { routeModeRef.current = routeMode; }, [routeMode]);

  // Refs so the once-registered draw handlers always read current state/context.
  const drawSpecRef  = useRef(null);
  const measureRef   = useRef(null);
  const editingIdRef = useRef(null);
  const snapRef      = useRef(false);
  const ownerRef     = useRef({ ownerType, ownerId });
  const reloadRef    = useRef(null);
  // `toast` from useToast is a fresh object on every provider render, so it must
  // NOT appear in effect/callback deps — that would re-fire fetches in a loop.
  const toastRef     = useRef(toast);
  toastRef.current   = toast;
  useEffect(() => { drawSpecRef.current  = drawSpec;  }, [drawSpec]);
  useEffect(() => { measureRef.current   = measure;   }, [measure]);
  useEffect(() => { editingIdRef.current = editingId; }, [editingId]);
  useEffect(() => { snapRef.current      = snap;      }, [snap]);
  useEffect(() => { ownerRef.current = { ownerType, ownerId }; }, [ownerType, ownerId]);

  const ready = mapReady === true;

  // ── Load features ─────────────────────────────────────────
  const reload = useCallback(async () => {
    if (!ownerType || !ownerId) return;
    try {
      const data = await api.get(`map-features?ownerType=${ownerType}&ownerId=${ownerId}`);
      setFc(data || { type: 'FeatureCollection', features: [] });
    } catch (e) { toastRef.current.error(e.message); }
  }, [ownerType, ownerId]);
  useEffect(() => { reloadRef.current = reload; }, [reload]);

  useEffect(() => { if (ready) reload(); }, [ready, reload]);

  // If the parcel-boundary draw takes over the shared draw control, drop our
  // draw intent so its draw.create isn't mistaken for an annotation. Don't touch
  // drawRef here — that would wipe the parcel's in-progress geometry.
  useEffect(() => {
    if (parcelDrawActive) { setDrawSpec(null); setMeasure(null); setMeasureResult(null); setEditingId(null); }
  }, [parcelDrawActive]);

  // ── Render features on the map ────────────────────────────
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    setupAnnotationLayers(mapRef.current, fc);
  }, [fc, ready, mapRef]);

  // ── Click-to-select annotation ────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || map._annotationClickRegistered) return;
    map._annotationClickRegistered = true;
    const layers = ['annotations-fill', 'annotations-line', 'annotations-points'];
    layers.forEach(id => {
      map.on('click', id, (e) => {
        const props = e.features?.[0]?.properties;
        if (props?.mapFeatureId) setSelectedId(props.mapFeatureId);
      });
      map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', id, () => { map.getCanvas().style.cursor = ''; });
    });
  }, [ready, mapRef]);

  // ── Draw lifecycle (shared draw.create / draw.update handlers) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || map._annotationDrawRegistered) return;
    map._annotationDrawRegistered = true;

    map.on('draw.create', async (e) => {
      const spec = drawSpecRef.current;
      const meas = measureRef.current;
      const feature = e.features?.[0];
      if (!feature) return;

      // Measurement — ephemeral; show the result, leave the shape until cleared.
      if (meas) {
        const turf = await ensureTurf();
        if (meas.kind === 'line') {
          const miles = turf.length(feature, { units: 'miles' });
          setMeasureResult({ kind: 'line', text: fmtDistance(miles) });
        } else {
          const acres = sqMetersToAcres(turf.area(feature));
          setMeasureResult({ kind: 'area', text: `${acres.toFixed(2)} ac` });
        }
        return;
      }

      // Persisted annotation.
      if (!spec) return;
      const cat = catFor(spec.category);
      const { ownerType: ot, ownerId: oid } = ownerRef.current;
      let geometry = feature.geometry;
      if (snapRef.current) {
        const turf = await ensureTurf();
        geometry = snapGeometry(turf, geometry, getSnapTargets(map, turf));
      }
      try {
        await api.post('map-features', {
          ownerType: ot, ownerId: oid,
          featureKind: spec.kind,
          category: spec.category,
          name: cat.label,
          geometry,
          style: { color: cat.color, fillColor: cat.color, strokeColor: cat.color, opacity: 0.35, strokeWidth: 2.5 },
        });
        drawRef.current?.deleteAll();
        drawRef.current?.changeMode('simple_select');
        setDrawSpec(null);
        reloadRef.current?.();
        toastRef.current.success(`${cat.label} added.`);
      } catch (err) { toastRef.current.error(err.message); }
    });

    // Vertex edit — persist updated geometry.
    map.on('draw.update', async (e) => {
      const id = editingIdRef.current;
      const feature = e.features?.[0];
      if (!id || !feature) return;
      try {
        await api.put(`map-features/${id}`, { geometry: feature.geometry });
        reloadRef.current?.();
      } catch (err) { toastRef.current.error(err.message); }
    });
  }, [ready, mapRef, drawRef]);

  // ── Circle / square radial-drag ───────────────────────────
  // Press on the center, drag out to set radius, release to save. We drive this
  // directly with map mouse events (no MapboxDraw custom mode) for reliability.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !shapeMode) return;
    let center = null, dragging = false, radiusM = 0, turf = null;
    ensureTurf().then(t => { turf = t; });
    map.dragPan.disable();
    map.getCanvas().style.cursor = 'crosshair';

    const buildShape = (c, edge) => {
      if (!turf) return null;
      radiusM = turf.distance(turf.point(c), turf.point(edge), { units: 'meters' });
      if (radiusM < 0.5) return null;
      if (shapeMode === 'circle') return turf.circle(c, radiusM / 1000, { steps: 64, units: 'kilometers' }).geometry;
      // Square = axis-aligned bbox of the equivalent circle.
      const circ = turf.circle(c, radiusM / 1000, { steps: 16, units: 'kilometers' });
      return turf.bboxPolygon(turf.bbox(circ)).geometry;
    };
    const onDown = (e) => { center = [e.lngLat.lng, e.lngLat.lat]; dragging = true; };
    const onMove = (e) => {
      if (!dragging || !center) return;
      const geom = buildShape(center, [e.lngLat.lng, e.lngLat.lat]);
      if (geom) showPreview(map, geom, '#22d3ee');
    };
    const onUp = async (e) => {
      if (!dragging || !center) return;
      dragging = false;
      const geom = buildShape(center, [e.lngLat.lng, e.lngLat.lat]);
      clearPreview(map);
      setShapeMode(null);
      if (!geom) return;
      const { ownerType: ot, ownerId: oid } = ownerRef.current;
      const cat = catFor('other');
      try {
        await api.post('map-features', {
          ownerType: ot, ownerId: oid,
          featureKind: shapeMode === 'circle' ? 'circle' : 'polygon',
          category: 'other',
          name: shapeMode === 'circle' ? 'Circle' : 'Square',
          geometry: geom,
          style: { color: cat.color, fillColor: cat.color, strokeColor: cat.color, opacity: 0.35, strokeWidth: 2.5, radiusMeters: Math.round(radiusM) },
        });
        reloadRef.current?.();
        toastRef.current.success(`${shapeMode === 'circle' ? 'Circle' : 'Square'} added.`);
      } catch (err) { toastRef.current.error(err.message); }
    };

    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    return () => {
      map.off('mousedown', onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
      clearPreview(map);
    };
  }, [shapeMode, ready, mapRef]);

  // ── Route builder: collect waypoint clicks while routeMode is on ──
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !routeMode) return;
    map.getCanvas().style.cursor = 'crosshair';
    const onClick = (e) => setRoutePts(prev => [...prev, [e.lngLat.lng, e.lngLat.lat]]);
    map.on('click', onClick);
    return () => { map.off('click', onClick); map.getCanvas().style.cursor = ''; };
  }, [routeMode, ready, mapRef]);

  // Recompute the snapped route whenever waypoints change.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    if (routePts.length < 2) { setRouteGeom(null); clearPreview(map); return; }
    let cancelled = false;
    (async () => {
      try {
        const geom = await fetchDirections(routePts, routeProfile);
        if (cancelled) return;
        setRouteGeom(geom);
        if (geom) showPreview(map, geom, '#2563eb');
      } catch (err) { toastRef.current.error(err.message); }
    })();
    return () => { cancelled = true; };
  }, [routePts, routeProfile, ready, mapRef]);

  // ── Actions ───────────────────────────────────────────────
  const startDraw = (category) => {
    if (!drawRef.current || parcelDrawActive) return;
    const cat = catFor(category);
    cancelEdit();
    setShapeMode(null);
    setMeasure(null); setMeasureResult(null);
    setDrawSpec({ category, kind: cat.kind });
    drawRef.current.deleteAll();
    drawRef.current.changeMode(modeFor(cat.kind));
  };

  const startMeasure = (kind) => {
    if (!drawRef.current || parcelDrawActive) return;
    cancelEdit();
    setShapeMode(null);
    setDrawSpec(null);
    setMeasure({ kind }); setMeasureResult(null);
    drawRef.current.deleteAll();
    drawRef.current.changeMode(kind === 'line' ? 'draw_line_string' : 'draw_polygon');
  };

  const startShape = (kind) => {
    if (!drawRef.current || parcelDrawActive) return;
    cancelEdit();
    setDrawSpec(null); setMeasure(null); setMeasureResult(null);
    drawRef.current.deleteAll();
    drawRef.current.changeMode('simple_select');
    setShapeMode(prev => prev === kind ? null : kind);
  };

  const cancelDrawOrMeasure = () => {
    if (drawRef.current) { drawRef.current.deleteAll(); drawRef.current.changeMode('simple_select'); }
    setDrawSpec(null); setMeasure(null); setMeasureResult(null);
  };

  const selectedFeature = fc.features.find(f => f.properties?.mapFeatureId === selectedId) || null;

  const startEdit = () => {
    const feat = selectedFeature;
    if (!feat || !drawRef.current) return;
    cancelDrawOrMeasure();
    setEditingId(selectedId);
    drawRef.current.deleteAll();
    const ids = drawRef.current.add(feat);
    drawRef.current.changeMode('direct_select', { featureId: ids[0] });
  };

  const cancelEdit = () => {
    if (editingIdRef.current && drawRef.current) {
      drawRef.current.deleteAll();
      drawRef.current.changeMode('simple_select');
    }
    setEditingId(null);
  };

  const renameSelected = async () => {
    if (!selectedFeature) return;
    const current = selectedFeature.properties?.name || '';
    const name = window.prompt('Label / name', current);
    if (name == null) return;
    setBusy(true);
    try { await api.put(`map-features/${selectedId}`, { name }); reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    if (!selectedFeature) return;
    if (!window.confirm('Delete this feature?')) return;
    setBusy(true);
    try {
      await api.del(`map-features/${selectedId}`);
      setSelectedId(null);
      cancelEdit();
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Merge a style patch into the selected feature and persist.
  const updateStyle = async (patch) => {
    if (!selectedFeature) return;
    const merged = { ...(selectedFeature.properties?.style || {}), ...patch };
    setBusy(true);
    try { await api.put(`map-features/${selectedId}`, { style: merged }); reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Subtract any overlapping annotation areas from the selected one — preview
  // first, then confirm to persist the trimmed boundary.
  const trimSelected = async () => {
    const feat = selectedFeature;
    if (!feat) return;
    if (feat.geometry?.type !== 'Polygon' && feat.geometry?.type !== 'MultiPolygon') {
      toast.info('Trim works on area features.'); return;
    }
    const turf = await ensureTurf();
    const others = fc.features.filter(f =>
      f.properties?.mapFeatureId !== selectedId &&
      (f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'));
    let result = feat, changed = false;
    for (const o of others) {
      try {
        if (turf.booleanIntersects(result, o)) {
          const diff = turf.difference(turf.featureCollection([result, o]));
          if (diff) { result = diff; changed = true; }
        }
      } catch { /* skip invalid */ }
    }
    if (!changed) { toast.info('No overlaps to trim.'); return; }
    setTrimPreview({ id: selectedId, geometry: result.geometry });
    showPreview(mapRef.current, result.geometry, '#ef4444');
  };
  const applyTrim = async () => {
    if (!trimPreview) return;
    setBusy(true);
    try {
      await api.put(`map-features/${trimPreview.id}`, { geometry: trimPreview.geometry });
      clearPreview(mapRef.current); setTrimPreview(null); reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const cancelTrim = () => { clearPreview(mapRef.current); setTrimPreview(null); };

  // ── Route builder ─────────────────────────────────────────
  const startRoute = () => {
    if (parcelDrawActive) return;
    cancelDrawOrMeasure(); cancelEdit(); setShapeMode(null);
    setRoutePts([]); setRouteGeom(null);
    setRouteMode(true);
  };
  const finishRoute = () => {
    setRouteMode(false);
    setRoutePts([]); setRouteGeom(null);
    clearPreview(mapRef.current);
  };
  const undoRoutePt = () => setRoutePts(prev => prev.slice(0, -1));
  const saveRoute = async () => {
    if (!routeGeom) return;
    const { ownerType: ot, ownerId: oid } = ownerRef.current;
    const cat = catFor('route');
    setBusy(true);
    try {
      await api.post('map-features', {
        ownerType: ot, ownerId: oid, featureKind: 'line', category: 'route', name: 'Route',
        geometry: routeGeom,
        style: { color: cat.color, strokeColor: cat.color, strokeWidth: 3 },
        props: { profile: routeProfile, waypoints: routePts },
      });
      finishRoute();
      reload();
      toast.success('Route saved.');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // ── Live GPS tracking ─────────────────────────────────────
  const startTracking = () => {
    const map = mapRef.current;
    if (!navigator.geolocation) { toast.error('Geolocation is not available in this browser.'); return; }
    trackRef.current = []; setTrackLen(0);
    setTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        trackRef.current.push([pos.coords.longitude, pos.coords.latitude]);
        setTrackLen(trackRef.current.length);
        if (trackRef.current.length >= 2)
          showPreview(map, { type: 'LineString', coordinates: [...trackRef.current] }, '#16a34a');
        if (trackRef.current.length === 1) map?.easeTo({ center: trackRef.current[0], zoom: 16 });
      },
      (err) => { toast.error(`GPS error: ${err.message}`); },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 },
    );
  };
  const stopTracking = () => {
    if (watchIdRef.current != null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    setTracking(false);
  };
  const saveTrack = async () => {
    if (trackRef.current.length < 2) { toast.info('Not enough points to save a track yet.'); return; }
    const { ownerType: ot, ownerId: oid } = ownerRef.current;
    setBusy(true);
    try {
      await api.post('map-features', {
        ownerType: ot, ownerId: oid, featureKind: 'line', category: 'track', name: 'Track',
        geometry: { type: 'LineString', coordinates: [...trackRef.current] },
        style: { color: '#16a34a', strokeColor: '#16a34a', strokeWidth: 3 },
      });
      trackRef.current = []; setTrackLen(0); clearPreview(mapRef.current);
      reload();
      toast.success('Track saved.');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // ── Import a KMZ/KML — save every shape as a feature ──────
  const onKmzImport = async (e) => {
    const file = e.target.files?.[0];
    if (kmzInputRef.current) kmzInputRef.current.value = ''; // allow re-selecting same file
    if (!file) return;
    const { ownerType: ot, ownerId: oid } = ownerRef.current;
    setKmzBusy(true);
    try {
      const parsed = await kmzToGeoJSON(file);
      const features = kmzFeaturesToPayloads(parsed);
      if (!features.length) { toast.error('No shapes found in this file.'); return; }
      await api.post('map-features/bulk', { ownerType: ot, ownerId: oid, features });
      reload();
      toast.success(`Imported ${features.length} shape${features.length !== 1 ? 's' : ''}.`);
    } catch (err) { toast.error(err.message || 'Failed to read file.'); }
    finally { setKmzBusy(false); }
  };

  // Stop the GPS watch if the panel unmounts.
  useEffect(() => () => { if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current); }, []);

  if (mapReady === 'no-token') return null;

  const drawing = !!drawSpec || !!measure || !!editingId || !!shapeMode || routeMode;

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, background: B.white, overflow: 'hidden' }}>
      <button onClick={() => setShow(s => !s)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', border: 'none', background: B.surfaceMuted, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: B.navy,
      }}>
        <span>Land Features {fc.features.length > 0 && <span style={{ color: B.textMd, fontWeight: 400 }}>({fc.features.length})</span>}</span>
        <span style={{ color: B.textLt, display: 'inline-flex' }}><Icon name={show ? 'chevronDown' : 'chevronRight'} size={13} /></span>
      </button>

      {show && (
        <div style={{ padding: 14 }}>
          {parcelDrawActive && (
            <div style={{ fontSize: 12, color: B.amber, marginBottom: 10 }}>
              Finish or cancel the parcel boundary first.
            </div>
          )}

          {/* Draw tools */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: B.textLt, marginBottom: 8 }}>Add Feature</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {ANNOTATION_CATEGORIES.map(cat => (
              <button key={cat.key} onClick={() => startDraw(cat.key)} disabled={parcelDrawActive || drawing}
                title={`Draw ${cat.label}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 16,
                  border: `1px solid ${drawSpec?.category === cat.key ? cat.color : B.border}`,
                  background: drawSpec?.category === cat.key ? cat.color : B.white,
                  color: drawSpec?.category === cat.key ? '#fff' : B.textMd,
                  fontSize: 12, fontWeight: 600, cursor: (parcelDrawActive || drawing) ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', opacity: (parcelDrawActive || (drawing && drawSpec?.category !== cat.key)) ? 0.5 : 1,
                }}>
                <span aria-hidden>{cat.icon}</span>{cat.label}
              </button>
            ))}
          </div>

          {/* Shapes + snap */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, alignItems: 'center' }}>
            <Btn size="sm" variant={shapeMode === 'circle' ? 'primary' : 'ghost'} disabled={parcelDrawActive || (drawing && shapeMode !== 'circle')} onClick={() => startShape('circle')}><Icon name="circle" size={13} /> Circle</Btn>
            <Btn size="sm" variant={shapeMode === 'square' ? 'primary' : 'ghost'} disabled={parcelDrawActive || (drawing && shapeMode !== 'square')} onClick={() => startShape('square')}><Icon name="square" size={13} /> Square</Btn>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto', fontSize: 12, color: B.textMd, cursor: 'pointer' }}>
              <input type="checkbox" checked={snap} onChange={e => setSnap(e.target.checked)} />
              Snap to edges
            </label>
          </div>

          {/* Import KMZ/KML — saves every shape (points, lines, polygons) as features */}
          <div style={{ marginBottom: 14 }}>
            <input ref={kmzInputRef} type="file" accept=".kmz,.kml" onChange={onKmzImport} style={{ display: 'none' }} />
            <Btn size="sm" variant="ghost" disabled={parcelDrawActive || kmzBusy} onClick={() => kmzInputRef.current?.click()}>
              {kmzBusy ? 'Importing…' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="upload" size={13} /> Import KMZ/KML</span>}
            </Btn>
            <span style={{ fontSize: 11, color: B.textLt, marginLeft: 8 }}>Saves all shapes to the map.</span>
          </div>

          {/* Measure tools */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: B.textLt, marginBottom: 8 }}>Measure</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <Btn size="sm" variant={measure?.kind === 'line' ? 'primary' : 'ghost'} disabled={parcelDrawActive || (drawing && !measure)} onClick={() => startMeasure('line')}><Icon name="ruler" size={13} /> Distance</Btn>
            <Btn size="sm" variant={measure?.kind === 'area' ? 'primary' : 'ghost'} disabled={parcelDrawActive || (drawing && !measure)} onClick={() => startMeasure('area')}><Icon name="area" size={13} /> Area</Btn>
          </div>
          {measureResult && (
            <div style={{ fontSize: 13, fontWeight: 700, color: B.navy, marginBottom: 8 }}>
              {measureResult.kind === 'line' ? 'Distance: ' : 'Area: '}{measureResult.text}
            </div>
          )}

          {/* Tools */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: B.textLt, margin: '4px 0 8px' }}>Tools</div>

          {/* Route builder */}
          {!routeMode ? (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
              <Btn size="sm" variant="ghost" disabled={parcelDrawActive || drawing || tracking} onClick={startRoute}><Icon name="route" size={13} /> Route</Btn>
              <select value={routeProfile} onChange={e => setRouteProfile(e.target.value)}
                style={{ fontSize: 12, padding: '4px 6px', border: `1px solid ${B.border}`, borderRadius: 6, fontFamily: 'inherit', color: B.text }}>
                <option value="driving">Driving</option>
                <option value="walking">Walking</option>
                <option value="cycling">Cycling</option>
              </select>
            </div>
          ) : (
            <div style={{ padding: '8px 10px', background: B.surfaceMuted, borderRadius: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: B.textMd, marginBottom: 6 }}>
                Click the map to add waypoints ({routePts.length}). Route snaps to roads.
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Btn size="sm" variant="ghost" disabled={!routePts.length} onClick={undoRoutePt}>Undo point</Btn>
                <Btn size="sm" disabled={!routeGeom || busy} onClick={saveRoute}>Save route</Btn>
                <Btn size="sm" variant="ghost" onClick={finishRoute}>Done</Btn>
              </div>
            </div>
          )}

          {/* Live GPS tracking */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {!tracking
              ? <Btn size="sm" variant="ghost" disabled={parcelDrawActive || routeMode} onClick={startTracking}><Icon name="record" size={13} /> Track GPS</Btn>
              : <Btn size="sm" variant="danger" onClick={stopTracking}><Icon name="stop" size={12} /> Stop ({trackLen})</Btn>}
            {(tracking || trackLen >= 2) && (
              <Btn size="sm" disabled={busy || trackLen < 2} onClick={saveTrack}>Save track</Btn>
            )}
          </div>

          {/* Active draw banner */}
          {drawing && !routeMode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: B.surfaceMuted, borderRadius: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: B.textMd }}>
                {editingId ? 'Editing shape — drag vertices, then Done.'
                  : shapeMode ? `Press & drag on the map to size the ${shapeMode}.`
                  : measure ? 'Click to measure; double-click to finish.'
                  : `Drawing ${catFor(drawSpec.category).label}…${snap ? ' (snapping)' : ''}`}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {editingId
                  ? <Btn size="sm" variant="secondary" onClick={cancelEdit}>Done</Btn>
                  : shapeMode
                  ? <Btn size="sm" variant="ghost" onClick={() => setShapeMode(null)}>Cancel</Btn>
                  : <Btn size="sm" variant="ghost" onClick={cancelDrawOrMeasure}>Cancel</Btn>}
              </div>
            </div>
          )}

          {/* Selected feature actions */}
          {selectedFeature && !drawing && (
            <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 10, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: B.text, marginBottom: 2 }}>
                {selectedFeature.properties?.name || catFor(selectedFeature.properties?.category).label}
              </div>
              <div style={{ fontSize: 11, color: B.textLt, marginBottom: 8 }}>
                {catFor(selectedFeature.properties?.category).label} · {selectedFeature.properties?.featureKind}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <Btn size="sm" variant="ghost" onClick={renameSelected} disabled={busy}>Rename</Btn>
                <Btn size="sm" variant="ghost" onClick={startEdit} disabled={busy}>Edit shape</Btn>
                {(selectedFeature.properties?.featureKind === 'polygon' || selectedFeature.properties?.featureKind === 'circle') && (
                  <Btn size="sm" variant="ghost" onClick={trimSelected} disabled={busy}>Trim overlap</Btn>
                )}
                <Btn size="sm" variant="danger" onClick={deleteSelected} disabled={busy}>Delete</Btn>
              </div>

              {/* Style editor */}
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: B.textLt, marginBottom: 4 }}>Color</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                  {COLOR_PALETTE.map(hex => (
                    <button key={hex} title={hex} onClick={() => updateStyle({ color: hex, fillColor: hex, strokeColor: hex })} disabled={busy}
                      style={{ width: 18, height: 18, borderRadius: 4, cursor: 'pointer',
                        background: hex, border: `2px solid ${(selectedFeature.properties?.style?.fillColor || selectedFeature.properties?.style?.color) === hex ? B.navy : B.border}` }} />
                  ))}
                </div>
                {(selectedFeature.properties?.featureKind === 'polygon' || selectedFeature.properties?.featureKind === 'circle') && (
                  <>
                    <div style={{ fontSize: 11, color: B.textLt, marginBottom: 4 }}>Fill</div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      {[['none', 'Solid'], ['hatch', 'Hatch'], ['cross', 'Cross']].map(([h, lbl]) => {
                        const active = (selectedFeature.properties?.style?.hatch || 'none') === h;
                        return <Btn key={h} size="sm" variant={active ? 'primary' : 'ghost'} disabled={busy} onClick={() => updateStyle({ hatch: h })}>{lbl}</Btn>;
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: B.textLt, marginBottom: 4 }}>Opacity</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[0.2, 0.35, 0.5, 0.7].map(op => {
                        const active = (selectedFeature.properties?.style?.opacity ?? 0.35) === op;
                        return <Btn key={op} size="sm" variant={active ? 'primary' : 'ghost'} disabled={busy} onClick={() => updateStyle({ opacity: op })}>{Math.round(op * 100)}%</Btn>;
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Trim preview confirm */}
          {trimPreview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fef2f2', border: `1px solid ${B.red}`, borderRadius: 8, marginTop: 10 }}>
              <span style={{ fontSize: 12, color: B.text }}>Preview shows the trimmed boundary.</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="ghost" onClick={cancelTrim} disabled={busy}>Cancel</Btn>
                <Btn size="sm" disabled={busy} onClick={applyTrim}>Save trim</Btn>
              </div>
            </div>
          )}

          {/* Feature list */}
          {fc.features.length > 0 && !drawing && (
            <div style={{ marginTop: 12, maxHeight: 180, overflowY: 'auto' }}>
              {fc.features.map(f => {
                const p = f.properties || {};
                const cat = catFor(p.category);
                const sel = p.mapFeatureId === selectedId;
                return (
                  <button key={p.mapFeatureId} onClick={() => setSelectedId(p.mapFeatureId)} style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                    background: sel ? B.tealLt : 'transparent', marginBottom: 2,
                  }}>
                    <span aria-hidden>{cat.icon}</span>
                    <span style={{ flex: 1, fontSize: 12, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name || cat.label}
                    </span>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: (p.style?.color || cat.color) }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
